import type { CSSProperties } from 'react';
import { resolveDividerStyle, resolveDividerThickness } from './divider-attributes';

/**
 * The rules that stop a host page's CSS reset having an opinion about the line.
 *
 * Every popular reset has already decided what an `<hr>` looks like, and they
 * disagree: Bootstrap draws one at a quarter opacity, older versions paint it
 * as a background bar with no border at all, browsers with no reset at all
 * still paint the inset 3D groove from the days of Netscape. A widget is
 * dropped into a page whose stylesheet it has never seen, so the only line
 * that renders the same everywhere is one whose box is stated outright.
 *
 * These are inline styles, so they win against anything the host declares —
 * and column styles are spread over them afterwards, so they lose to anything
 * the author sets.
 */
const hostResetStyles: CSSProperties = {
  boxSizing: 'border-box',
  // The line is the top border. The element itself has no content, and the
  // other three sides are what the browser's own inset groove is made of.
  height: 0,
  borderRightStyle: 'none',
  borderBottomStyle: 'none',
  borderLeftStyle: 'none',
  backgroundColor: 'transparent',
  opacity: 1,
  // Spacing around a divider belongs to the row's gap and the column's margin,
  // the same as it does for every other component. Zero here is a decision,
  // not an omission: the alternative is spacing that depends on which reset
  // the host page happens to load.
  marginBlock: 0,
  // Only visible once the author narrows the column: an inset rule centres
  // rather than hugging the left edge, which is what a shortened divider is
  // almost always for.
  marginInline: 'auto',
};

/**
 * The divider's contribution to its own inline style.
 *
 * Written entirely in per-side longhands. Column styles arrive as the
 * all-sides `borderWidth` / `borderStyle` / `borderColor` — different keys,
 * which is what matters: spread later they are appended after these rather
 * than replacing them in place, so an author who sets Border on the column
 * gets a border and not a silent no-op.
 */
export function buildDividerStyles(componentAttributes?: {
  [key: string]: any;
}): CSSProperties {
  return {
    ...hostResetStyles,
    borderTopStyle: resolveDividerStyle(componentAttributes),
    borderTopWidth: resolveDividerThickness(componentAttributes),
    // Inherited, so Text Color on the column colours the line — and a column
    // that says nothing gets a rule in the same ink as the words around it.
    borderTopColor: 'currentColor',
  };
}
